import { useMemo, type ReactNode } from "react"
import { GridItem } from "@chakra-ui/react"
import { useParams } from "react-router-dom"
import type { SystemInstallationHeadlines } from "../types/SystemView"
import FigureGrid from "@hydrologiq/shared/figures/generic/FigureGrid"
import NextRefuelFigure from "@hydrologiq/shared/figures/headlines/NextRefuelFigure"
import HydrogenLevelFigure from "@hydrologiq/shared/figures/headlines/HydrogenLevelFigure"
import HydrogenUsedFigure from "@hydrologiq/shared/figures/headlines/HydrogenUsedFigure"
import EnergyGeneratedFigure from "@hydrologiq/shared/figures/headlines/EnergyGeneratedFigure"
import CurrentPowerFigure from "@hydrologiq/shared/figures/headlines/CurrentPowerFigure"
import HybridLoadFigure from "@hydrologiq/shared/figures/headlines/HybridLoadFigure"
import EmissionsFactorFigure from "@hydrologiq/shared/figures/headlines/EmissionsFactorFigure"
import AlertsPerformanceFigure from "@hydrologiq/shared/figures/asset/AlertsPerformanceFigure"
import TotalEmissionsFigure from "@hydrologiq/shared/figures/headlines/TotalEmissionsFigure"
// --- Burnaston overview cards (see docs/current/burnaston-cards) ---
import VehiclesToFillFigure from "@hydrologiq/shared/figures/headlines/VehiclesToFillFigure"
import ElectrolyserStatusFigure from "@hydrologiq/shared/figures/headlines/ElectrolyserStatusFigure"
import ElectrolyserStackFigure from "@hydrologiq/shared/figures/headlines/ElectrolyserStackFigure"
import HoursToFillFigure from "@hydrologiq/shared/figures/headlines/HoursToFillFigure"
import HydrogenProducedFigure from "@hydrologiq/shared/figures/headlines/HydrogenProducedFigure"
import { useSystemViewData, useSystemInfo } from "../providers/helpers/useSystem"
import { collectElectrolysers, collectElectrolyserStacks } from "../utils/electrolysers"
import { FIGURE_KEYS, FIGURE_TYPE_TO_KEY, type FigureKey } from "@hydrologiq/shared/figures/figureKeys"
import { Figure as FigureType } from "@hydrologiq/schemas/1.0.0/installationPage"
import combinedFigures from "../components/monitoring/FiguresDefault.json"
import { isHydrogenSystem, isHydrogenFigure } from "../utils/hydrogenSystem"

/**
 * Determine whether a figure should be shown.
 *
 * When the system has a `figures` configuration (from the database), we use
 * the same filtering logic as monitoring: convert Figure.type → FigureKey via
 * FIGURE_TYPE_TO_KEY and check membership. When no configuration is defined,
 * fall back to the full default set (show all).
 */
function shouldShowFigure(figureKey: FigureKey, allowedKeys: Set<FigureKey>): boolean {
  return allowedKeys.has(figureKey)
}

export interface SystemHeadlineFiguresProps {
  headlines: SystemInstallationHeadlines | null | undefined
  loading?: boolean
  /** Passed to FigureGrid; use 0 when parent wraps spacing for ResizeObserver measurement */
  figureGridMb?: number
  /** System-level figures configuration from the database (same as monitoring uses).
   *  When provided, only figures whose type appears in this list will be shown.
   *  When absent/empty, falls back to the default set (all figures). */
  figures?: FigureType[]
}

/**
 * Headline strip for the system flow view: data from getSystemView.installationHeadlines;
 * visibility driven by the system's `figures` database configuration (matching monitoring),
 * falling back to the full default set when no configuration is defined.
 */
function SystemHeadlineFigures({ headlines, loading, figureGridMb = 3, figures }: SystemHeadlineFiguresProps) {
  const { systemId } = useParams<{ systemId: string }>()
  // Card 1 & 2 are computed on the frontend; systemViewData supplies the electrolyser nodes.
  const systemViewData = useSystemViewData()
  const info = useSystemInfo()

  const isHydrogen = useMemo(
    () => isHydrogenSystem(systemViewData, info),
    [systemViewData, info]
  )

  // Use the system's `figures` config if available, otherwise fall back to FiguresDefault.json.
  // Then map the types to FigureKeys. This ensures that explicitly configured Burnaston cards
  // (which intentionally don't exist in FiguresDefault.json) still get rendered.
  // When not a hydrogen system, hydrogen cards are filtered out so pure diesel / battery systems
  // never show placeholder hydrogen figures.
  const effectiveFigures = useMemo(() => {
    const baseFigures = figures ?? (combinedFigures as FigureType[])
    if (isHydrogen) {
      return baseFigures
    }
    return baseFigures.filter((f) => !isHydrogenFigure(f))
  }, [figures, isHydrogen])

  const allowedKeys = useMemo(
    () =>
      new Set<FigureKey>(
        effectiveFigures
          .map((f) => FIGURE_TYPE_TO_KEY[f.type])
          .filter((key): key is FigureKey => key !== undefined)
      ),
    [effectiveFigures]
  )

  if (loading || !headlines) {
    return <FigureGrid loading mb={figureGridMb} />
  }

  const availableFigures: { key: string; node: ReactNode }[] = []

  // Alerts card is always shown when headline data is available
  if (headlines.alerts) {
    availableFigures.push({
      key: "alerts",
      node: (
        <AlertsPerformanceFigure
          alerts={headlines.alerts.alerts}
          totalCount={headlines.alerts.total}
          loading={false}
          compact
          systemId={systemId}
          linkToAlertsView={true}
        />
      ),
    })
  }

  if (isHydrogen && shouldShowFigure(FIGURE_KEYS.NEXT_REFUEL, allowedKeys)) {
    availableFigures.push({
      key: "nextRefuel",
      node: (
        <NextRefuelFigure
          data={headlines.nextRefuel ?? undefined}
          loading={false}
          isPlaceholder={!headlines.nextRefuel?.available}
          compact
        />
      ),
    })
  }

  if (isHydrogen && shouldShowFigure(FIGURE_KEYS.HYDROGEN_LEVEL, allowedKeys)) {
    availableFigures.push({
      key: "hydrogenLevel",
      node: (
        <HydrogenLevelFigure
          data={headlines.hydrogenLevel ?? undefined}
          loading={false}
          isPlaceholder={!headlines.hydrogenLevel?.available}
          compact
        />
      ),
    })
  }

  if (isHydrogen && shouldShowFigure(FIGURE_KEYS.HYDROGEN_USED, allowedKeys)) {
    availableFigures.push({
      key: "hydrogenUsed",
      node: (
        <HydrogenUsedFigure
          data={headlines.hydrogenUsed ?? undefined}
          loading={false}
          isPlaceholder={!headlines.hydrogenUsed?.available}
          compact
        />
      ),
    })
  }

  if (shouldShowFigure(FIGURE_KEYS.ENERGY, allowedKeys)) {
    availableFigures.push({
      key: "energy",
      node: (
        <EnergyGeneratedFigure
          data={headlines.energy ?? undefined}
          loading={false}
          isPlaceholder={!headlines.energy?.available}
          compact
        />
      ),
    })
  }

  if (shouldShowFigure(FIGURE_KEYS.EMISSIONS_FACTOR, allowedKeys)) {
    availableFigures.push({
      key: "emissionsFactor",
      node: (
        <EmissionsFactorFigure
          data={headlines.emissionsFactor ?? undefined}
          loading={false}
          isPlaceholder={!headlines.emissionsFactor?.available}
          compact
        />
      ),
    })
  }

  if (shouldShowFigure(FIGURE_KEYS.CURRENT_POWER, allowedKeys)) {
    availableFigures.push({
      key: "currentPower",
      node: (
        <CurrentPowerFigure
          data={headlines.currentPower ?? undefined}
          loading={false}
          isPlaceholder={!headlines.currentPower?.available}
          compact
        />
      ),
    })
  }

  // Current Load (site load — role='load' power). Reuses HybridLoadFigure; gated on the
  // HYBRID_LOAD key, which the `CurrentLoad` figure type maps to via FIGURE_TYPE_TO_KEY.
  if (shouldShowFigure(FIGURE_KEYS.HYBRID_LOAD, allowedKeys)) {
    availableFigures.push({
      key: "currentLoad",
      node: (
        <HybridLoadFigure
          data={headlines.currentLoad ?? undefined}
          loading={false}
          isPlaceholder={!headlines.currentLoad?.available}
          compact
        />
      ),
    })
  }

  if (shouldShowFigure(FIGURE_KEYS.CO2E, allowedKeys)) {
    availableFigures.push({
      key: "co2e",
      node: (
        <TotalEmissionsFigure
          data={headlines.co2e ?? undefined}
          loading={loading}
          isPlaceholder={!headlines.co2e?.available}
          compact
        />
      ),
    })
  }

  // --- Burnaston overview cards (see docs/current/burnaston-cards) ---
  // Shown only when the figure key is in `allowedKeys` (i.e. in FiguresDefault.json + the
  // system's `figures` config). Cards 1 & 2 are computed on the frontend; Cards 3 & 4 read
  // values served by core in installationHeadlines.

  // Card 1 — Capacity to Fill (frontend calc from the current installation weight).
  if (isHydrogen && shouldShowFigure(FIGURE_KEYS.VEHICLES_TO_FILL, allowedKeys)) {
    availableFigures.push({
      key: "vehiclesToFill",
      node: (
        <VehiclesToFillFigure
          currentWeightKg={headlines.hydrogenLevel?.value ?? null}
          loading={loading}
          compact
        />
      ),
    })
  }

  // Card 2 — electrolysers. When they are grouped parent:child we summarise the whole stack under
  // the product's roll-up rules; otherwise there is nothing to roll up, so we page through the
  // units individually with ElectrolyserStatusFigure.
  if (isHydrogen && shouldShowFigure(FIGURE_KEYS.ELECTROLYSER_STATUS, allowedKeys)) {
    const stacks = collectElectrolyserStacks(systemViewData)
    const electrolysers = collectElectrolysers(systemViewData)
    if (stacks.length > 0) {
      availableFigures.push({
        key: "electrolyserStack",
        node: <ElectrolyserStackFigure stacks={stacks} loading={loading} compact />,
      })
    } else if (electrolysers.length > 0) {
      availableFigures.push({
        key: "electrolyserStatus",
        node: <ElectrolyserStatusFigure electrolysers={electrolysers} loading={loading} compact />,
      })
    }
  }

  if (isHydrogen && shouldShowFigure(FIGURE_KEYS.HOURS_TO_FILL, allowedKeys)) {
    availableFigures.push({
      key: "hoursToFill",
      node: (
        <HoursToFillFigure
          data={headlines.hoursToFill ?? undefined}
          loading={loading}
          isPlaceholder={!headlines.hoursToFill?.available}
          compact
        />
      ),
    })
  }

  if (isHydrogen && shouldShowFigure(FIGURE_KEYS.HYDROGEN_PRODUCED, allowedKeys)) {
    availableFigures.push({
      key: "hydrogenProduced",
      node: (
        <HydrogenProducedFigure
          data={headlines.hydrogenProduced ?? undefined}
          loading={loading}
          isPlaceholder={!headlines.hydrogenProduced?.available}
          compact
        />
      ),
    })
  }

  if (availableFigures.length === 0) {
    return null
  }

  return (
    <FigureGrid mb={figureGridMb} trimmed>
      {availableFigures.map(({ key, node }) => (
        <GridItem key={key} display="flex" w="100%">
          {node}
        </GridItem>
      ))}
    </FigureGrid>
  )
}

export default SystemHeadlineFigures
